import React from "react";
import { Routes as ReactRoutes, Route } from "react-router-dom";

const AccessData = React.lazy(() => import(/* webpackChunkName: "AccessData" */'./pages/AccessData'));
const Confirmation = React.lazy(() => import(/* webpackChunkName: "Confirmation" */'./pages/Confirmation'));
const ErrorComponent = React.lazy(() => import(/* webpackChunkName: "ErrorComponent" */'./pages/Error'));
const Review = React.lazy(() => import(/* webpackChunkName: "Review" */'./pages/Review'));
const SelectResponse = React.lazy(() => import(/* webpackChunkName: "SelectResponse" */'./pages/SelectResponse'));
const SigneeForm = React.lazy(() => import(/* webpackChunkName: "SigneeForm" */'./pages/SigneeForm'));
const StudentInfo = React.lazy(() => import(/* webpackChunkName: "StudentInfo" */'./pages/StudentInfo'));
const TicketsAndAttendeeForm = React.lazy(() => import(/* webpackChunkName: "TicketsAndAttendeeForm" */'./pages/TicketsAndAttendeeForm'));
const PublicLanding = React.lazy(() => import(/* webpackChunkName: "PublicLanding" */'./pages/LandingPages/PublicLanding'));
const SigneeInfo = React.lazy(() => import(/* webpackChunkName: "SigneeInfo" */'./pages/SigneeInfo'));
const PDFDownloadLanding = React.lazy(() => import(/* webpackChunkName: "PDFDownloadLanding" */'./pages/LandingPages/PDFDownloadLanding'));
const LegacyHashLanding = React.lazy(() => import(/* webpackChunkName: "LegacyHashLanding" */'./pages/LandingPages/LegacyHashLanding'));
const RosterTokenLanding = React.lazy(() => import(/* webpackChunkName: "RosterTokenLanding" */'./pages/LandingPages/RosterTokenLanding'));
const PayNowTokenLanding = React.lazy(() => import(/* webpackChunkName: "PayNowTokenLanding" */'./pages/LandingPages/PayNowTokenLanding'));


const RosterBase = "/r/:hashedId"; // SMPC-14859: legacy hash routes — served by LegacyHashLanding during grace period, shows link-retired page once isRetired
const PayNowBase = "/rp/:hashedId"; // SMPC-14859: legacy hash routes — served by LegacyHashLanding during grace period, shows link-retired page once isRetired
const RosterTokenBase = "/rt/:countryCode/:token";
const PayNowTokenBase = "/rtp/:countryCode/:token";
const PDFDownloadBase = "/rd/:countryCode/:hashedId";
const RouteBase = "/:hashedId/:countryCode";
const OrgRouteBase = "/:hashedId/:countryCode/:hashedOrgId";
const PreviewBase = `${RouteBase}/preview`;

const AccessDataRoute = "access-data";
const AttendeeInformationRoute = "attendees";
const SigneeFormRoute = "signee-form";
const SelectResponseRoute = "select-response";
const AttendeeFormRoute = "attendee-form";
const ReviewRoute = "review";
const ConfirmationRoute = "confirmation";
const SigneeInfoRoute = "signee-info";

export interface PreviewProps {
  isPreview?: boolean;
}

const Routes = (): JSX.Element => (
  <ReactRoutes>
    {/* Rostered response — token-based links (new) */}
    <Route path={RosterTokenBase} element={<RosterTokenLanding />} />
    <Route path={`${RosterTokenBase}/${AccessDataRoute}`} element={<AccessData />} />
    <Route path={`${RosterTokenBase}/${SigneeInfoRoute}`} element={<SigneeInfo />} />
    <Route path={`${RosterTokenBase}/${SelectResponseRoute}`} element={<SelectResponse />} />
    <Route path={`${RosterTokenBase}/${AttendeeInformationRoute}`} element={<StudentInfo />} />
    <Route path={`${RosterTokenBase}/${SigneeFormRoute}`} element={<SigneeForm />} />
    <Route
      path={`${RosterTokenBase}/${AttendeeFormRoute}/:attendeeIndex`}
      element={<TicketsAndAttendeeForm />}
    />
    <Route path={`${RosterTokenBase}/${ReviewRoute}`} element={<Review />} />
    <Route path={`${RosterTokenBase}/${ConfirmationRoute}`} element={<Confirmation />} />

    {/* Rostered and Staff Form Library — hash-based links (SMPC-14859: conditional retirement via DisabledLegacyLinks flag) */}
    {/* Only the base route needs LegacyHashLanding (retirement check + RosterLanding initialization). */}
    {/* Sub-routes render the same content components as the token-based routes, consistent with */}
    {/* how /rt/:countryCode/:token/* is structured. Direct navigation (refresh, bookmarks) to a */}
    {/* sub-route will land here with default UrlParams — the same gap that exists on token routes. */}
    {/* Wrapping sub-routes in LegacyHashLanding would cause a render loop: LegacyHashLanding → */}
    {/* RosterLanding → <Navigate to="/r/{hash}/access-data"> → same route → repeat → blank screen. */}
    <Route path={RosterBase} element={<LegacyHashLanding linkType="roster" />} />
    <Route path={`${RosterBase}/${AccessDataRoute}`} element={<AccessData />} />
    <Route path={`${RosterBase}/${SigneeInfoRoute}`} element={<SigneeInfo />} />
    <Route path={`${RosterBase}/${SelectResponseRoute}`} element={<SelectResponse />} />
    <Route path={`${RosterBase}/${AttendeeInformationRoute}`} element={<StudentInfo />} />
    <Route path={`${RosterBase}/${SigneeFormRoute}`} element={<SigneeForm />} />
    <Route
      path={`${RosterBase}/${AttendeeFormRoute}/:attendeeIndex`}
      element={<TicketsAndAttendeeForm />}
    />
    <Route path={`${RosterBase}/${ReviewRoute}`} element={<Review />} />
    <Route path={`${RosterBase}/${ConfirmationRoute}`} element={<Confirmation />} />

    {/* PDF Download Link */}
    <Route path={PDFDownloadBase} element={<PDFDownloadLanding />} />

    {/* Public Link */}
    <Route path={RouteBase} element={<PublicLanding />} />
    <Route path={`${RouteBase}/${SigneeInfoRoute}`} element={<SigneeInfo />} />
    <Route path={`${RouteBase}/${SelectResponseRoute}`} element={<SelectResponse />} />
    <Route path={`${RouteBase}/${AttendeeInformationRoute}`} element={<StudentInfo />} />
    <Route path={`${RouteBase}/${SigneeFormRoute}`} element={<SigneeForm />} />
    <Route
      path={`${RouteBase}/${AttendeeFormRoute}/:attendeeIndex`}
      element={<TicketsAndAttendeeForm />}
    />
    <Route path={`${RouteBase}/${ReviewRoute}`} element={<Review />} />
    <Route path={`${RouteBase}/${ConfirmationRoute}`} element={<Confirmation />} />

    {/* Pay Now — token-based links (new) */}
    <Route path={PayNowTokenBase} element={<PayNowTokenLanding />} />
    <Route path={`${PayNowTokenBase}/${ReviewRoute}`} element={<Review />} />
    <Route path={`${PayNowTokenBase}/${ConfirmationRoute}`} element={<Confirmation />} />

    {/* Pay Now — hash-based links (SMPC-14859: conditional retirement via DisabledLegacyLinks flag) */}
    <Route path={PayNowBase} element={<LegacyHashLanding linkType="payNow" />} />
    <Route path={`${PayNowBase}/${ReviewRoute}`} element={<Review />} />
    <Route path={`${PayNowBase}/${ConfirmationRoute}`} element={<Confirmation />} />

    {/* Public link for school via district form */}
    <Route path={OrgRouteBase} element={<PublicLanding />} />
    <Route path={`${OrgRouteBase}/${SigneeInfoRoute}`} element={<SigneeInfo />} />
    <Route path={`${OrgRouteBase}/${SelectResponseRoute}`} element={<SelectResponse />} />
    <Route path={`${OrgRouteBase}/${AttendeeInformationRoute}`} element={<StudentInfo />} />
    <Route path={`${OrgRouteBase}/${SigneeFormRoute}`} element={<SigneeForm />} />
    <Route
      path={`${OrgRouteBase}/${AttendeeFormRoute}/:attendeeIndex`}
      element={<TicketsAndAttendeeForm />}
    />
    <Route path={`${OrgRouteBase}/${ReviewRoute}`} element={<Review />} />
    <Route path={`${OrgRouteBase}/${ConfirmationRoute}`} element={<Confirmation />} />

    {/* Preview */}
    <Route path={`${PreviewBase}`} element={<PublicLanding isPreview />} />
    <Route path={`${PreviewBase}/${SigneeInfoRoute}`} element={<SigneeInfo isPreview />} />
    <Route
      path={`${PreviewBase}/${AttendeeInformationRoute}`}
      element={<StudentInfo isPreview />}
    />
    <Route path={`${PreviewBase}/${SigneeFormRoute}`} element={<SigneeForm isPreview />} />
    <Route
      path={`${PreviewBase}/${AttendeeFormRoute}/:attendeeIndex`}
      element={<TicketsAndAttendeeForm isPreview />}
    />
    <Route path={`${PreviewBase}/${ReviewRoute}`} element={<Review isPreview />} />
    <Route path={`${PreviewBase}/${ConfirmationRoute}`} element={<Confirmation isPreview />} />

    {/* catch */}
    <Route path="/" element={<ErrorComponent error="Incorrect URL" />} />
  </ReactRoutes>
);

export {
  Routes,
  AccessDataRoute,
  AttendeeInformationRoute,
  SigneeFormRoute,
  SelectResponseRoute,
  AttendeeFormRoute,
  ConfirmationRoute,
  SigneeInfoRoute,
  ReviewRoute,
};
